import React, { memo } from 'react';
import { useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import UpdateCurrencyButton from '@swf/sports/components/UpdateCurrencyButton/UpdateCurrencyButton';

import useDisplayPriceChanges from '../../../../../../hooks/useDisplayPriceChanges';
import useTotals from '../../../../../../hooks/useTotals';

import AcceptPriceButton from './components/AcceptPriceButton/AcceptPriceButton';
import ConvertFundsButton from './components/ConvertFundsButton/ConvertFundsButton';
import DepositButton from './components/DepositButton/DepositButton';
import LoginButton from './components/LoginButton/LoginButton';
import PlaceBetButton from './components/PlaceBetButton/PlaceBetButton';

const ActionButton: React.FC = () => {
  const { displayFooterPriceChanges } = useDisplayPriceChanges();
  const { totalStake } = useTotals();
  const { values: formValues } = useFormState({ subscription: { values: true } });

  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const autoConvertFunds = useSelector(select.account.getMultiCurrencyPreferences).convertBuyIns;
  const preferredCurrencyBalance = useSelector(select.account.getPreferredCurrencyBalance) || 0;
  const totalBalance = useSelector(select.account.getTotalBalance) || 0;
  const extraWallets = useSelector(select.betSlip.wallets);
  const isCurrencyRestricted = useSelector(select.betSlip.isCurrencyRestricted);

  const promotions = Object.values(formValues).flatMap(
    (value) => (value.bonusCash || value.freeBet) ?? []
  );
  const promotionalStake =
    promotions.reduce((acc, walletId) => acc + (extraWallets[walletId]?.amount ?? 0), 0) * 100;

  const totalStakeMinusPromotions = totalStake - promotionalStake;

  const availableFunds = autoConvertFunds ? totalBalance : preferredCurrencyBalance;
  const hasSufficientFunds = availableFunds >= totalStakeMinusPromotions;

  if (displayFooterPriceChanges) {
    return <AcceptPriceButton />;
  }

  if (isCurrencyRestricted) {
    return <UpdateCurrencyButton />;
  }

  if (!isAuthenticated) {
    return <LoginButton />;
  }

  if (!hasSufficientFunds && !autoConvertFunds && totalBalance >= totalStakeMinusPromotions) {
    return <ConvertFundsButton />;
  }

  if (!hasSufficientFunds) {
    return <DepositButton />;
  }

  return <PlaceBetButton />;
};

export default memo(ActionButton);
